import type { Foto, FotoSoort, Registratie, RegistratieVersie, Woning } from '@/types/domain';

/**
 * Bron van registraties. Mock- en Supabase-implementatie voldoen beide aan
 * dit contract, zodat de UI niet weet waar de data vandaan komt.
 */
export interface RegistratieService {
  haalAlle(): Promise<Registratie[]>;
  /** Maakt aan of werkt bij; geeft de opgeslagen versie terug. */
  opslaan(registratie: Registratie): Promise<Registratie>;
  verwijderen(registratie: Registratie): Promise<void>;

  /**
   * Live meekijken bij wat anderen opslaan.
   *
   * Twee coaches werken tegelijk in het veld. Zonder dit ziet de een pas na
   * het verversen van de pagina dat de ander een woning heeft afgehandeld —
   * en loopt hij het risico dezelfde deur nog eens te bellen. Niet elke bron
   * ondersteunt dit; ontbreekt het, dan werkt de app gewoon zonder.
   */
  abonneerOpWijzigingen?(
    bijWijziging: (registratie: Registratie) => void,
    bijVerwijderen: (id: string) => void
  ): () => void;
}

/** Fotoservice: online opslag of lokale data-urls. */
export interface FotoService {
  upload(registratie: Registratie, bestand: File, soort: FotoSoort): Promise<Foto>;
  verwijderen(registratie: Registratie, foto: Foto): Promise<void>;
  /** Tijdelijke, tonbare link naar de foto. */
  weergaveUrl(foto: Foto): Promise<string>;
}

/** Eerdere versies van een registratie, nieuwste eerst. */
export interface HistorieService {
  haalVoor(registratieId: string): Promise<RegistratieVersie[]>;
}

/** Bron van BAG-woningen voor de kaart en de zoekfunctie. */
export interface WoningService {
  /**
   * Alle woningen in het plangebied, in één keer.
   *
   * Eerder werd er per kaartbeeld geladen, maar dan kapt de BAG-dienst de
   * lijst af bij een ruim beeld: je zag de helft van de punten, en na
   * inzoomen opeens meer. Het plangebied is klein en vast, dus alles in één
   * keer ophalen is eenvoudiger én betrouwbaarder.
   */
  haalWoningen(): Promise<Woning[]>;
  /** Vrije zoekopdracht op straat + huisnummer. */
  zoek(term: string): Promise<Woning[]>;
  /** Dichtstbijzijnde adres bij een punt; voor handmatig geplaatste punten. */
  adresBijPunt(latitude: number, longitude: number): Promise<Woning | null>;
}

/** Wat iemand mag: registreren, of alleen meekijken zonder persoonsgegevens. */
export type Rol = 'coach' | 'gemeente';

/** De ingelogde gebruiker. */
export interface Gebruiker {
  id: string;
  email: string;
  naam: string;
  rol: Rol;
}

export interface AuthService {
  huidigeGebruiker(): Promise<Gebruiker | null>;
  inloggen(email: string, wachtwoord: string): Promise<Gebruiker>;
  uitloggen(): Promise<void>;
  /** Roept de callback aan bij in- en uitloggen; geeft een opzegfunctie terug. */
  abonneer(bijWijziging: (gebruiker: Gebruiker | null) => void): () => void;
}

export class ServiceFout extends Error {
  constructor(
    message: string,
    readonly oorzaak?: unknown
  ) {
    super(message);
    this.name = 'ServiceFout';
  }
}
